import { useCallback, useEffect, useState, type KeyboardEvent } from 'react';
import { focusMoveFor, rovingTabIndex } from '@visious/core';

/**
 * §9's roving focus for a feed grid — the DOM half. Every *decision* is `focusMoveFor` in core,
 * including the two rules that are easy to get wrong (`→` never moves you down a row, and `↓` from a
 * column the last row is too short to have still reaches the last item). What is here is only what
 * cannot be pure: which element to call `.focus()` on, and how many columns the browser laid out.
 *
 * The column count is *measured* rather than derived from a breakpoint. The grid is
 * `repeat(auto-fill, minmax(…, 1fr))`, so the browser already decided; asking it is both shorter and
 * the only version that stays right when the shell changes the content width or `[data-scale='tv']`
 * grows every length by 25 %. Guessing it would put `↓` one row off — the kind of bug that looks like
 * a random focus jump.
 */
export interface GridFocus {
  /**
   * A callback ref, and deliberately a `useState` setter rather than a `useRef`: the effect that
   * measures the column count has to *re-run* when the element arrives, and a ref assignment does not
   * re-render, so with a ref the first measurement would race the first paint.
   */
  readonly attachGrid: (node: HTMLUListElement | null) => void;
  readonly columns: number;
  /** For `rovingTabIndex(index, …)` at each card: exactly one card in the grid is tabbable. */
  readonly focusedIndex: number;
  readonly tabIndexFor: (index: number) => 0 | -1;
  readonly onKeyDown: (event: KeyboardEvent<HTMLUListElement>) => void;
  /** A click or a Tab landed on a card — it becomes the roving member so the arrows start there. */
  readonly onFocusIndex: (index: number) => void;
}

function countColumns(grid: HTMLElement): number {
  // `getComputedStyle` reports the *resolved* track list ("260px 260px 260px"), which is the answer
  // `auto-fill` produced for the width the grid actually got.
  const tracks = globalThis.getComputedStyle(grid).gridTemplateColumns;
  const columns = tracks.split(' ').filter((track) => track !== '').length;
  return columns > 0 ? columns : 1;
}

/**
 * The card's real link — the one the roving `tabIndex` is on, found by the property that
 * distinguishes it: the thumbnail's duplicate copy is `aria-hidden`.
 *
 * The fallback is for a row with **no link at all**: a playlist entry whose
 * video YouTube has deleted is deliberately not an anchor (the id is real, so a link would look live
 * and land on "Video not found"). Such a row carries the roving `tabIndex` on the `<li>` itself.
 * Without this arm, `↓` onto one moves the tab stop and leaves focus behind — the list would look
 * frozen for exactly one keypress and then resume.
 */
function cardLink(grid: HTMLElement, index: number): HTMLElement | null {
  const card = grid.children[index];
  if (!(card instanceof HTMLElement)) return null;
  const link = card.querySelector<HTMLElement>('a:not([aria-hidden="true"])');
  if (link !== null) return link;
  // `hasAttribute` rather than `card.tabIndex >= 0`: a non-roving row is rendered `tabindex="-1"`,
  // which is precisely the state that is unreachable by Tab and reachable by `.focus()`.
  return card.hasAttribute('tabindex') ? card : null;
}

/**
 * Which card the key came from, read off the DOM rather than out of React state — and this is the
 * one decision in the file worth arguing for.
 *
 * State lags by a render, so a handler that closed over `focusedIndex` answers with wherever focus
 * was when it was *created*. Held-down arrow keys repeat far faster than that, and every repeat after
 * the first then recomputes the same move from the same stale origin: the first press steps one card
 * and the rest do nothing at all. Measured in a driven browser exactly that way — three `→` in a row
 * left focus on card 1, and `↑` from card 3 was declined as "not ours" because the closure still
 * thought it was on card 0.
 *
 * The DOM cannot lag: focus *is* where the browser put it. So the origin comes from the event's own
 * target and state is left with the single job it is actually good for — deciding which card carries
 * `tabIndex={0}` on the next render.
 */
function indexOfEvent(grid: HTMLElement, target: EventTarget | null): number {
  if (!(target instanceof Element)) return -1;
  const card = target.closest('li');
  return card === null ? -1 : [...grid.children].indexOf(card);
}

export function useGridFocus(count: number): GridFocus {
  const [grid, setGrid] = useState<HTMLUListElement | null>(null);
  const [columns, setColumns] = useState(1);
  const [focusedIndex, setFocusedIndex] = useState(0);

  // A refetch can shrink the list under a focused index. Adjusted during render rather than in an
  // effect, so no frame is ever painted with a tabIndex pointing past the end of the grid.
  if (focusedIndex >= count && count > 0) setFocusedIndex(0);

  useEffect(() => {
    if (grid === null) return;
    const measure = () => setColumns(countColumns(grid));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [grid, count]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLUListElement>) => {
      // Every modifier combination belongs to the browser or the platform (`playback/input.ts` draws
      // the same line): Shift+Arrow extends a selection, and ⌘←/→ is history navigation.
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (grid === null) return;

      const next = focusMoveFor(event.key, {
        count,
        // Where focus *is*, not where the last render thought it was — see `indexOfEvent`.
        index: indexOfEvent(grid, event.target),
        orientation: 'grid',
        columns,
      });
      // `null` means "not ours": an unhandled `↓` at the end of the grid must still scroll the page,
      // and on a TV it must still let the focus engine leave the group.
      if (next === null) return;

      event.preventDefault();
      setFocusedIndex(next);
      // Moved *now* rather than in an effect after the re-render. It works because the target already
      // carries `tabindex="-1"`, which is exactly the state that is unreachable by Tab and reachable
      // by `.focus()` — so there is nothing to wait for, and no ref holding a pending index between
      // renders. Roving `tabIndex` then catches up on the render this schedules.
      cardLink(grid, next)?.focus();
    },
    [columns, count, grid],
  );

  return {
    attachGrid: setGrid,
    columns,
    focusedIndex,
    tabIndexFor: (index: number) => rovingTabIndex(index, focusedIndex),
    onKeyDown,
    onFocusIndex: setFocusedIndex,
  };
}
